// =============================================================================
// CHARTE GRAPHIQUE B8ILD - STYLES PARTAGÉS POUR LES PDFs
// =============================================================================

import { StyleSheet } from '@react-pdf/renderer';

// Couleurs B8ild
export const COLORS = {
  // Couleurs principales
  bleuRoi: '#1A365D',      // Headers, titres, textes importants
  dore: '#D4AF37',         // Accents, highlights, éléments premium
  blanc: '#FFFFFF',        // Fond
  grisClair: '#F3F4F6',    // Fonds de sections
  grisTexte: '#4B5563',    // Texte secondaire
  grisFonce: '#374151',    // Texte principal
  noir: '#0F172A',         // Texte emphase

  // Couleurs de statut
  vertSucces: '#10B981',
  vertClair: '#D1FAE5',
  orangeAttention: '#F59E0B',
  orangeClair: '#FEF3C7',
  rougeAlerte: '#EF4444',
  rougeClair: '#FEE2E2',
  bleuClair: '#DBEAFE',
};

// Styles partagés entre les différents PDFs
export const sharedStyles = StyleSheet.create({
  // ============= PAGE =============
  page: {
    paddingTop: 0,
    paddingBottom: 60,
    paddingHorizontal: 0,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: COLORS.grisFonce,
    backgroundColor: COLORS.blanc,
  },

  // ============= HEADER =============
  header: {
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 20,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    letterSpacing: -0.5, // tracking-tight comme sur la landing page
  },
  headerRight: {
    textAlign: 'right',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 10,
    color: COLORS.dore,
  },
  bandeDore: {
    height: 4,
    backgroundColor: COLORS.dore,
  },

  // ============= CONTENU =============
  content: {
    paddingHorizontal: 40,
    paddingTop: 25,
  },

  // ============= ENCART CHANTIER =============
  chantierBox: {
    backgroundColor: COLORS.bleuRoi,
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  chantierNom: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 12,
  },
  chantierInfoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  chantierLabel: {
    fontSize: 10,
    color: COLORS.dore,
    width: 80,
  },
  chantierValue: {
    fontSize: 10,
    color: COLORS.blanc,
    flex: 1,
  },

  // ============= KPI CARDS =============
  kpiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 10,
  },
  kpiCard: {
    flex: 1,
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  kpiCardHighlight: {
    borderWidth: 2,
    borderColor: COLORS.dore,
  },
  kpiEmoji: {
    fontSize: 20,
    marginBottom: 6,
  },
  kpiTitle: {
    fontSize: 8,
    color: COLORS.grisTexte,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  kpiValue: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.noir,
  },
  kpiSubvalue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginTop: 2,
  },
  kpiBadge: {
    marginTop: 8,
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 10,
  },
  kpiBadgeText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
  },

  // ============= SÉPARATEUR =============
  separator: {
    height: 2,
    backgroundColor: COLORS.dore,
    marginVertical: 15,
    opacity: 0.4,
  },

  // ============= SECTIONS =============
  section: {
    marginBottom: 18,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionEmoji: {
    fontSize: 14,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },

  // ============= TABLEAUX =============
  table: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableHeaderCell: {
    color: COLORS.blanc,
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tableRowAlt: {
    backgroundColor: COLORS.grisClair,
  },
  tableRowTotal: {
    backgroundColor: COLORS.bleuClair,
    borderBottomWidth: 0,
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
  },
  tableCellRight: {
    flex: 1,
    textAlign: 'right',
    fontSize: 9,
  },
  tableCellBold: {
    fontFamily: 'Helvetica-Bold',
  },

  // ============= COULEURS ÉCARTS =============
  ecartPositif: {
    color: COLORS.vertSucces,
    fontFamily: 'Helvetica-Bold',
  },
  ecartNegatif: {
    color: COLORS.rougeAlerte,
    fontFamily: 'Helvetica-Bold',
  },
  ecartNeutre: {
    color: COLORS.orangeAttention,
    fontFamily: 'Helvetica-Bold',
  },

  // ============= BOXES STATS =============
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  statsBox: {
    flex: 1,
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  statsValue: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    marginBottom: 4,
  },
  statsLabel: {
    fontSize: 8,
    color: COLORS.grisTexte,
    textAlign: 'center',
    textTransform: 'uppercase',
  },

  // ============= ALERTES =============
  alertBox: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderLeftWidth: 4,
  },
  alertSuccess: {
    backgroundColor: COLORS.vertClair,
    borderLeftColor: COLORS.vertSucces,
  },
  alertWarning: {
    backgroundColor: COLORS.orangeClair,
    borderLeftColor: COLORS.orangeAttention,
  },
  alertDanger: {
    backgroundColor: COLORS.rougeClair,
    borderLeftColor: COLORS.rougeAlerte,
  },
  alertText: {
    fontSize: 10,
    color: COLORS.grisFonce,
  },
  alertTextBold: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
    marginBottom: 2,
  },

  // ============= FOOTER =============
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  footerBande: {
    height: 3,
    backgroundColor: COLORS.dore,
  },
  footerContent: {
    backgroundColor: COLORS.grisClair,
    paddingVertical: 12,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: COLORS.grisTexte,
  },
  footerPage: {
    fontSize: 8,
    color: COLORS.bleuRoi,
    fontFamily: 'Helvetica-Bold',
  },
  footerQuote: {
    fontSize: 8,
    color: COLORS.grisTexte,
    fontStyle: 'italic',
  },

  // ============= ENCART MARGE FINALE (Bilan) =============
  margeFinaleBox: {
    backgroundColor: COLORS.bleuRoi,
    borderRadius: 12,
    padding: 25,
    marginBottom: 20,
    alignItems: 'center',
    borderWidth: 3,
    borderColor: COLORS.dore,
  },
  margeFinaleLabel: {
    fontSize: 12,
    color: COLORS.blanc,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  margeFinaleValue: {
    fontSize: 36,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 4,
  },
  margeFinalePct: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.dore,
    marginBottom: 10,
  },
  margeFinaleStatus: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  margeFinaleStatusText: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
  },

  // ============= COMPARAISON PRÉVU/RÉEL =============
  comparaisonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    marginBottom: 15,
  },
  comparaisonBox: {
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    minWidth: 100,
  },
  comparaisonLabel: {
    fontSize: 9,
    color: COLORS.grisTexte,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  comparaisonValue: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  comparaisonUnit: {
    fontSize: 10,
    color: COLORS.grisTexte,
  },
  comparaisonArrow: {
    fontSize: 24,
    color: COLORS.dore,
  },

  // ============= VENTILATION COÛTS (Barres) =============
  ventilationBar: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'center',
  },
  ventilationLabel: {
    fontSize: 9,
    color: COLORS.grisFonce,
    width: 80,
  },
  ventilationBarContainer: {
    flex: 1,
    height: 16,
    backgroundColor: '#E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
    marginRight: 10,
  },
  ventilationBarFill: {
    height: 16,
    borderRadius: 8,
  },
  ventilationValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
    width: 70,
    textAlign: 'right',
  },
  ventilationPct: {
    fontSize: 8,
    color: COLORS.grisTexte,
    width: 35,
    textAlign: 'right',
  },

  // ============= ENSEIGNEMENTS =============
  enseignementBox: {
    backgroundColor: COLORS.bleuClair,
    borderRadius: 8,
    padding: 15,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.bleuRoi,
    marginBottom: 15,
  },
  enseignementTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    marginBottom: 8,
  },
  enseignementText: {
    fontSize: 10,
    color: COLORS.grisFonce,
    lineHeight: 1.5,
  },
  enseignementRatio: {
    fontSize: 9,
    color: COLORS.grisTexte,
    marginTop: 8,
    fontStyle: 'italic',
  },

  // ============= PROJECTION =============
  projectionBox: {
    backgroundColor: COLORS.grisClair,
    borderRadius: 8,
    padding: 15,
    borderWidth: 1,
    borderColor: COLORS.dore,
    borderStyle: 'dashed',
  },
  projectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    marginBottom: 10,
  },
  projectionItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  projectionBullet: {
    fontSize: 10,
    color: COLORS.dore,
    marginRight: 6,
  },
  projectionText: {
    fontSize: 10,
    color: COLORS.grisFonce,
    flex: 1,
  },
});

// =============================================================================
// RÉEXPORT DES FONCTIONS DE RENTABILITÉ DEPUIS LE FICHIER CENTRAL
// Tous les seuils sont définis dans calculs-rentabilite.ts (source unique de vérité)
// =============================================================================

export {
  getMargeConfig,
  getMargeStatus,
  getStatutRentabilite,
  SEUILS_MARGE,
  SEUILS_RENTABILITE,
  BENCHMARKS_BTP,
  MARGE_MOYENNE_BTP,
  type MargeConfig,
  type StatutRentabilite as MargeStatus,
} from '@/lib/calculs-rentabilite';
